import React from 'react';
import { Link, useLocation } from 'react-router-dom';

/**
 * ThemeLink component
 * 
 * A custom Link component that preserves URL parameters,
 * especially the theme parameter, across navigation.
 * 
 * @param {object} props - Component props
 * @param {string} props.to - Target URL
 * @param {React.ReactNode} props.children - Child elements
 * @returns {JSX.Element}
 */
const ThemeLink = ({ to, children, ...props }) => {
  const location = useLocation();
  
  // Function to preserve query parameters
  const preserveQueryParams = (targetPath) => {
    // Get current URL search params
    const currentParams = new URLSearchParams(location.search);
    
    // Only proceed if we have search params to preserve
    if (currentParams.toString()) {
      // If it's already a URL object with search params
      if (typeof targetPath === 'object' && targetPath.pathname) {
        // Create a new URL search params object from either target or current params
        const newSearchParams = new URLSearchParams(targetPath.search || '');
        
        // Preserve the theme parameter
        if (currentParams.has('theme') && !newSearchParams.has('theme')) {
          newSearchParams.set('theme', currentParams.get('theme'));
        }
        
        return {
          ...targetPath,
          search: newSearchParams.toString()
        };
      } else {
        // If it's a string path
        const hasQueryParams = targetPath.includes('?');
        
        // If target already has params, append theme param
        if (hasQueryParams) {
          const targetParams = new URLSearchParams(targetPath.split('?')[1]);
          
          // Only add theme if it doesn't already exist
          if (currentParams.has('theme') && !targetParams.has('theme')) {
            targetParams.set('theme', currentParams.get('theme'));
          }
          
          return `${targetPath.split('?')[0]}?${targetParams.toString()}`;
        } else {
          // No params in target, so add current params
          if (currentParams.has('theme')) {
            return `${targetPath}?theme=${currentParams.get('theme')}`;
          }
        }
      }
    }
    
    // If no params to preserve or target already has them, return as is
    return targetPath;
  };

  // Create a new "to" prop with preserved params
  const toWithParams = preserveQueryParams(to);

  return (
    <Link to={toWithParams} {...props}>
      {children}
    </Link>
  );
};

export default ThemeLink; 